import { runAz } from "./az.js";

export interface FounderIdentity {
  name: string;
  /** Best-effort contact email; "" when no usable candidate. */
  email: string;
}

/**
 * Derive a contact-email candidate from an Azure identity:
 *  1. the real `mail` attribute (work/school account), else
 *  2. un-mangle a B2B guest UPN — `jane_acme.com#EXT#@host` → `jane@acme.com`
 *     (Azure AD encodes the original email with `@`→`_` before `#EXT#`), else
 *  3. a UPN that already looks like a plain email, else
 *  4. "" (caller cold-asks).
 */
export function deriveEmailCandidate(raw: { mail?: string | null; upn?: string | null }): string {
  const mail = (raw.mail ?? "").trim();
  if (mail) return mail;
  const upn = (raw.upn ?? "").trim();
  if (!upn) return "";
  const ext = upn.indexOf("#EXT#");
  if (ext > 0) {
    const local = upn.slice(0, ext);                 // jane_acme.com
    const at = local.lastIndexOf("_");
    return at > 0 ? `${local.slice(0, at)}@${local.slice(at + 1)}` : "";
  }
  return upn.includes("@") ? upn : "";
}

/** Read the signed-in user's name + email candidate; degrades to empty on any failure. */
export async function getSignedInUserIdentity(
  runAzImpl: (args: string[]) => Promise<string> = runAz,
): Promise<FounderIdentity> {
  try {
    const raw = await runAzImpl([
      "ad", "signed-in-user", "show",
      "--query", "{displayName:displayName, mail:mail, upn:userPrincipalName}",
      "-o", "json",
    ]);
    const p = JSON.parse(raw) as { displayName?: string; mail?: string | null; upn?: string | null };
    return { name: (p.displayName ?? "").trim(), email: deriveEmailCandidate({ mail: p.mail, upn: p.upn }) };
  } catch {
    return { name: "", email: "" };
  }
}

/** The deploy-time {{founder_identity_note}} for Simple Stacey's instructions. */
export function composeFounderIdentityNote(id: FounderIdentity): string {
  if (id.name && id.email) {
    return `You're speaking with **${id.name}**, signed in to Azure (their email looks like **${id.email}**). ` +
      `Open by confirming both, in your own voice — e.g. "I see you're signed in as ${id.name}, and I'll use ${id.email} ` +
      `for your advisor handoffs and cost reports — is that right, or should I change either?" Record exactly what they approve or correct.`;
  }
  if (id.name) {
    return `You're speaking with **${id.name}** (signed in to Azure). Confirm their name, then ask for the best contact email ` +
      `to reach them (that's where advisor handoffs and cost reports go).`;
  }
  return `Ask the founder their name and the best contact email to reach them (that's where advisor handoffs and cost reports go).`;
}
